import { referrerHost } from "@/lib/share/events";

/**
 * First-touch marketing attribution. Captured ONCE, client-side, on ANY
 * first-party page — including the SEO content/marketing pages that never
 * mint the app's own `plotzio_anon_session` cookie (see session/anonSession.ts)
 * — so a visitor who reads an article today and enters the app tomorrow still
 * carries where they came from. The cookie is intentionally NOT HttpOnly:
 * both `app_opened` (events.ts) and the PostHog person `$set_once` at
 * identify() (client.ts) need to read it back client-side.
 *
 * "First touch" means WRITE ONCE, never overwrite — `captureFirstTouchIfAbsent`
 * no-ops if the cookie already exists, so a later direct/internal visit can
 * never clobber the original acquisition source.
 */

export const FIRST_TOUCH_COOKIE = "pz_first_touch";
const FIRST_TOUCH_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365; // 1 year, matches plotzio_anon_session

export interface FirstTouch {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  /** Bare referrer host only (never a full URL) — same no-PII posture as share_event's referrerHost. */
  referrer_host?: string;
  landing_path?: string;
  /** ISO timestamp of the first touch — the anchor time-to-convert (signup, checkout) is measured from. */
  touched_at: string;
}

/** Parse the first-touch cookie value out of a raw `document.cookie` string. Pure — independently testable. */
export function parseFirstTouchCookie(cookieString: string): FirstTouch | undefined {
  const match = cookieString.match(new RegExp(`(?:^|;\\s*)${FIRST_TOUCH_COOKIE}=([^;]+)`));
  if (!match) return undefined;
  try {
    const parsed: unknown = JSON.parse(decodeURIComponent(match[1]!));
    return parsed && typeof parsed === "object" ? (parsed as FirstTouch) : undefined;
  } catch {
    return undefined;
  }
}

/** Derive first-touch fields from a landing URL + referrer. Pure — independently testable. */
export function deriveFirstTouch(
  url: string,
  referrer: string | null | undefined,
  now: () => string = () => new Date().toISOString(),
): FirstTouch {
  const parsed = new URL(url);
  const out: FirstTouch = { landing_path: parsed.pathname, touched_at: now() };
  const utmSource = parsed.searchParams.get("utm_source");
  const utmMedium = parsed.searchParams.get("utm_medium");
  const utmCampaign = parsed.searchParams.get("utm_campaign");
  if (utmSource) out.utm_source = utmSource;
  if (utmMedium) out.utm_medium = utmMedium;
  if (utmCampaign) out.utm_campaign = utmCampaign;
  const host = referrerHost(referrer);
  if (host) out.referrer_host = host;
  return out;
}

/** Build the `document.cookie =` value. Not HttpOnly — client code reads it back. */
export function buildFirstTouchCookie(touch: FirstTouch, isSecureContext: boolean): string {
  const secure = isSecureContext ? "; Secure" : "";
  return `${FIRST_TOUCH_COOKIE}=${encodeURIComponent(JSON.stringify(touch))}; Path=/; Max-Age=${FIRST_TOUCH_COOKIE_MAX_AGE_SECONDS}; SameSite=Lax${secure}`;
}

/**
 * Capture first-touch attribution once per browser. Browser-only (no-op under
 * SSR). Returns the touch now stored — the pre-existing one if this browser
 * was already captured, or the freshly-derived one on a true first visit.
 */
export function captureFirstTouchIfAbsent(): FirstTouch | undefined {
  if (typeof document === "undefined") return undefined;
  const existing = parseFirstTouchCookie(document.cookie);
  if (existing) return existing;
  const touch = deriveFirstTouch(window.location.href, document.referrer || null);
  document.cookie = buildFirstTouchCookie(touch, window.location.protocol === "https:");
  return touch;
}

/** Read back the stored first-touch attribution. Browser-only. */
export function readFirstTouch(): FirstTouch | undefined {
  if (typeof document === "undefined") return undefined;
  return parseFirstTouchCookie(document.cookie);
}

/** Map first-touch fields onto `app_opened`'s already-registered optional props (registry.ts). */
export function firstTouchToAppOpenedProps(
  touch: FirstTouch | undefined,
): { referrer?: string; utm_source?: string; utm_medium?: string; utm_campaign?: string } {
  if (!touch) return {};
  const props: { referrer?: string; utm_source?: string; utm_medium?: string; utm_campaign?: string } = {};
  if (touch.referrer_host) props.referrer = touch.referrer_host;
  if (touch.utm_source) props.utm_source = touch.utm_source;
  if (touch.utm_medium) props.utm_medium = touch.utm_medium;
  if (touch.utm_campaign) props.utm_campaign = touch.utm_campaign;
  return props;
}

/**
 * Map first-touch fields onto PostHog `$set_once` person properties — set
 * exactly once on the FIRST identify() call for a person and never
 * overwritten afterward, PostHog's native mechanism for durable first-touch
 * attribution queryable on the person profile (joins to `user_signed_up` by
 * person). `initial_touched_at` is the anchor a time-to-convert query diffs
 * against any later event's timestamp for the same person (e.g.
 * `dateDiff('hour', toDateTime(properties.initial_touched_at), timestamp)`
 * against `user_signed_up` / `checkout_completed`) — segmentable by the other
 * `initial_*` channel properties in the same query. Returns undefined when
 * there's nothing to set (no cookie captured — SSR, cookies blocked, or an
 * /embed-only visitor).
 */
export function firstTouchToPersonPropsOnce(touch: FirstTouch | undefined): Record<string, string> | undefined {
  if (!touch) return undefined;
  const props: Record<string, string> = { initial_touched_at: touch.touched_at };
  if (touch.utm_source) props.initial_utm_source = touch.utm_source;
  if (touch.utm_medium) props.initial_utm_medium = touch.utm_medium;
  if (touch.utm_campaign) props.initial_utm_campaign = touch.utm_campaign;
  if (touch.referrer_host) props.initial_referrer_host = touch.referrer_host;
  if (touch.landing_path) props.initial_landing_path = touch.landing_path;
  return props;
}
